import "#packages/ecma402-abstract/types/date-time.js";
import { createMemoizedNumberFormat } from "#packages/ecma402-abstract/utils.js";
import Decimal from "@formatjs/bigdecimal";
import { ToLocalTime } from "#packages/ecma402-abstract/DateTimeFormat/ToLocalTime.js";
import { DATE_TIME_PROPS } from "#packages/ecma402-abstract/DateTimeFormat/utils.js";
export function getDayPeriodName(data, time, width = "short") {
	const value = ((time.hour * 60 + time.minute) * 60 + time.second) * 1e3 + time.millisecond;
	const rules = data.dayPeriodRules || [];
	// ECMA-402 §11.5.5, step 15.d.iii selects the localized day period and width.
	// LDML variable-period rules 2-4, 8: inclusive start, exclusive end, wrap midnight.
	// https://tc39.es/ecma402/#sec-formatdatetimepattern
	// https://github.com/tc39/ecma402/blob/b1c961988b9a07894b1dc3dc2b5626ea48387d61/spec/datetimeformat.html#L1386-L1390
	// https://unicode.org/reports/tr35/tr35-dates.html#Variable_periods
	// https://github.com/unicode-org/cldr/blob/acd6d88ae493633240e19a87a721076a8a75c310/docs/ldml/tr35-dates.md#L1360-L1375
	const rule = rules.find((rule) => rule.at === value) || rules.find((rule) => rule.from !== undefined && rule.before !== undefined && (rule.from < rule.before ? value >= rule.from && value < rule.before : value >= rule.from || value < rule.before));
	return rule && data.dayPeriods?.[width][rule.name] || (time.hour < 12 ? data.am : data.pm);
}
function pad(n) {
	if (n < 10) {
		return `0${n}`;
	}
	return String(n);
}
function offsetToGmtString(gmtFormat, hourFormat, offsetInMs, style) {
	const offsetInMinutes = Math.floor(offsetInMs / 6e4);
	const mins = Math.abs(offsetInMinutes) % 60;
	const hours = Math.floor(Math.abs(offsetInMinutes) / 60);
	const [positivePattern, negativePattern] = hourFormat.split(";");
	let offsetStr = "";
	let pattern = offsetInMs < 0 ? negativePattern : positivePattern;
	if (style === "long") {
		offsetStr = pattern.replace("HH", pad(hours)).replace("H", String(hours)).replace("mm", pad(mins)).replace("m", String(mins));
	} else if (mins || hours) {
		if (!mins) {
			pattern = pattern.replace(/:?m+/, "");
		}
		offsetStr = pattern.replace(/H+/, String(hours)).replace(/m+/, String(mins));
	}
	return gmtFormat.replace("{0}", offsetStr);
}
function createNumberFormatters(internalSlots) {
	const locale = internalSlots.locale;
	const nfOptions = Object.create(null);
	nfOptions.numberingSystem = internalSlots.numberingSystem;
	nfOptions.useGrouping = false;
	const nf = createMemoizedNumberFormat(locale, nfOptions);
	const nf2Options = Object.create(null);
	nf2Options.minimumIntegerDigits = 2;
	nf2Options.numberingSystem = internalSlots.numberingSystem;
	nf2Options.useGrouping = false;
	const nf2 = createMemoizedNumberFormat(locale, nf2Options);
	const fractionalSecondDigits = internalSlots.fractionalSecondDigits;
	let nf3;
	if (fractionalSecondDigits !== undefined) {
		const nf3Options = Object.create(null);
		nf3Options.minimumIntegerDigits = fractionalSecondDigits;
		nf3Options.numberingSystem = internalSlots.numberingSystem;
		nf3Options.useGrouping = false;
		nf3 = createMemoizedNumberFormat(locale, nf3Options);
	}
	return {
		nf,
		nf2,
		nf3
	};
}
/**
* https://tc39.es/ecma402/#sec-partitiondatetimepattern
* @param dtf
* @param x
*/
export function FormatDateTimePattern(dtf, patternParts, epochNanoseconds, isPlain, { getInternalSlots, localeData, getDefaultTimeZone, tzData, calendarData, rangeFormatOptions }) {
	/** IMPL START */
	const internalSlots = getInternalSlots(dtf);
	const dataLocale = internalSlots.dataLocale;
	const rootLocaleData = localeData[dataLocale];
	const dataLocaleData = rootLocaleData.calendarData?.[internalSlots.calendar] ?? rootLocaleData;
	/** IMPL END */
	// ECMA-402 11.5.5, steps 1-11 use identical NumberFormat options for
	// every record in this range. Resolve the existing memoized formatters once.
	// https://tc39.es/ecma402/#sec-formatdatetimepattern
	// https://github.com/tc39/ecma402/blob/b1c961988b9a07894b1dc3dc2b5626ea48387d61/spec/datetimeformat.html#L1356-L1372
	const numberFormatters = rangeFormatOptions?.numberFormatters || createNumberFormatters(internalSlots);
	if (rangeFormatOptions) rangeFormatOptions.numberFormatters = numberFormatters;
	const { nf, nf2, nf3 } = numberFormatters;
	const fractionalSecondDigits = internalSlots.fractionalSecondDigits;
	// ECMA-402 11.5.5, step 12: reuse the identical endpoint conversion from
	// PartitionDateTimeRangePattern (11.5.9, steps 7-8) within this call.
	// https://tc39.es/ecma402/#sec-partitiondatetimerangepattern
	// https://tc39.es/ecma402/#sec-formatdatetimepattern
	// https://github.com/tc39/ecma402/blob/b1c961988b9a07894b1dc3dc2b5626ea48387d61/spec/datetimeformat.html#L1373
	// https://github.com/tc39/ecma402/blob/b1c961988b9a07894b1dc3dc2b5626ea48387d61/spec/datetimeformat.html#L1529-L1530
	const tm = rangeFormatOptions?.localTime ?? ToLocalTime(
		epochNanoseconds,
		// @ts-ignore
		internalSlots.calendar,
		isPlain ? "+00:00" : internalSlots.timeZone,
		{
			tzData,
			calendarData
		}
	);
	const result = [];
	// Check if month is stand-alone (no other date fields like day, year, weekday)
	// Month grammar depends on the complete date pattern, including shared fields.
	// ECMA-402 11.5.5, step 15.f.x: https://tc39.es/ecma402/#sec-formatdatetimepattern
	// https://github.com/tc39/ecma402/blob/b1c961988b9a07894b1dc3dc2b5626ea48387d61/spec/datetimeformat.html#L1419
	const contextParts = rangeFormatOptions?.patternParts || patternParts;
	const hasMonth = contextParts.some((part) => part.type === "month");
	const hasOtherDateFields = contextParts.some((part) => part.type === "day" || part.type === "year" || part.type === "relatedYear" || part.type === "yearName" || part.type === "weekday" || part.type === "era");
	const isMonthStandalone = hasMonth && !hasOtherDateFields;
	for (const patternPart of patternParts) {
		const p = patternPart.type;
		if (p === "literal") {
			result.push({
				type: "literal",
				value: patternPart.value
			});
		} else if (p === "fractionalSecondDigits") {
			const v = new Decimal(tm.millisecond).times(Decimal.pow(10, (fractionalSecondDigits || 0) - 3)).floor().toNumber();
			result.push({
				type: "fractionalSecond",
				value: nf3.format(v)
			});
		} else if (p === "dayPeriod") {
			result.push({
				type: p,
				value: getDayPeriodName(dataLocaleData, tm, internalSlots.dayPeriod)
			});
		} else if (p === "timeZoneName") {
			const f = internalSlots.timeZoneName;
			let fv;
			const { timeZoneName, gmtFormat, hourFormat } = dataLocaleData;
			const timeZone = internalSlots.timeZone || getDefaultTimeZone();
			const timeZoneData = timeZoneName[timeZone];
			if (timeZoneData && timeZoneData[f]) {
				const names = timeZoneData[f];
				// GH #5114: If in DST and both standard/daylight names are the same,
				// fall back to GMT offset format (matches native browser behavior).
				// This handles locales where CLDR doesn't provide a daylight name.
				// NOTE: This is a formatjs implementation detail - ECMA-402 doesn't
				// explicitly specify behavior for missing DST names in locale data.
				if (tm.inDST && names.length >= 2 && names[0] === names[1]) {
					fv = offsetToGmtString(gmtFormat, hourFormat, tm.timeZoneOffset, f);
				} else {
					fv = names[+tm.inDST];
				}
			} else {
				// Fallback to gmtFormat
				fv = offsetToGmtString(gmtFormat, hourFormat, tm.timeZoneOffset, f);
			}
			result.push({
				type: p,
				value: fv
			});
		} else if (DATE_TIME_PROPS.indexOf(p) > -1) {
			let fv = "";
			let f = internalSlots[p];
			// @ts-ignore
			let v = tm[p];
			if (p === "year" && v <= 0 && (internalSlots.calendar === "gregory" || internalSlots.calendar === "iso8601")) {
				v = 1 - v;
			}
			if (p === "month") {
				v = (tm.monthNumber ?? v) + 1;
				if (internalSlots.calendar === "hebrew" && hasOtherDateFields && (f === "numeric" || f === "2-digit")) {
					f = "short";
				}
			}
			const hourCycle = internalSlots.hourCycle;
			if (p === "hour" && (hourCycle === "h11" || hourCycle === "h12")) {
				v = v % 12;
				if (v === 0 && hourCycle === "h12") {
					v = 12;
				}
			}
			// ECMA-402 11.5.5, step 15.f.vii.1 applies to single dates and ranges.
			// https://tc39.es/ecma402/#sec-formatdatetimepattern
			// https://github.com/tc39/ecma402/blob/b1c961988b9a07894b1dc3dc2b5626ea48387d61/spec/datetimeformat.html#L1406-L1407
			if (p === "hour" && hourCycle === "h24" && v === 0) {
				v = 24;
			}
			if (f === "numeric") {
				fv = nf.format(v);
			} else if (f === "2-digit") {
				fv = nf2.format(v);
				if (fv.length > 2) {
					// ECMA-402 11.5.5, steps 15.f.ix.2–4: keep the last two code points.
					// https://tc39.es/ecma402/#sec-formatdatetimepattern
					// https://github.com/tc39/ecma402/blob/b1c961988b9a07894b1dc3dc2b5626ea48387d61/spec/datetimeformat.html#L1412-L1417
					fv = Array.from(fv).slice(-2).join("");
				}
			} else if (f === "narrow" || f === "short" || f === "long") {
				if (p === "era") {
					fv = dataLocaleData[p][f][v];
				} else if (p === "month") {
					// Use stand-alone month form if available and month is displayed alone
					const monthData = isMonthStandalone && dataLocaleData.monthStandalone ? dataLocaleData.monthStandalone : dataLocaleData.month;
					fv = monthData[f][tm.monthNameIndex ?? v - 1];
				} else {
					fv = dataLocaleData[p][f][v];
				}
			}
			if (p === "month" && tm.leapMonth && dataLocaleData.leapMonthPatterns) {
				const patterns = dataLocaleData.leapMonthPatterns;
				const leapPattern = f === "numeric" || f === "2-digit" ? patterns.numeric : (isMonthStandalone ? patterns.standalone : patterns.format)[f];
				fv = leapPattern.replace("{0}", fv);
			}
			result.push({
				type: p,
				value: fv
			});
		} else if (p === "ampm") {
			const v = tm.hour;
			let fv;
			if (v > 11) {
				fv = dataLocaleData.pm;
			} else {
				fv = dataLocaleData.am;
			}
			result.push({
				type: "dayPeriod",
				value: fv
			});
		} else if (p === "relatedYear") {
			const v = tm.relatedYear;
			// @ts-ignore
			const fv = nf.format(v);
			result.push({
				// @ts-ignore TODO: Fix TS type
				type: "relatedYear",
				value: fv
			});
		} else if (p === "yearName") {
			const v = tm.yearName;
			// @ts-ignore
			const fv = dataLocaleData.yearNames[v];
			result.push({
				// @ts-ignore TODO: Fix TS type
				type: "yearName",
				value: fv
			});
		}
	}
	return result;
}
